/**
 * One owner of the board's URL state (docs/ux/pipeline.md "Forms and
 * filters"): tab, view or status, tile, doctor, referrer, q, sort and dir.
 * Unknown values are dropped and the URL rewritten in place; the desktop
 * bar and the phone sheet share the same setters, so pinned tabs deep-link.
 */

import { ALL_STATUSES, type Status } from '@beam/core/status';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { MatterRow } from '@/app/types';
import { PIPELINE_VIEWS, SORT_KEYS, TILES, defaultOrder, matchesQuery, tileMatches, type SortKey, type TileId, type ViewId } from './views';

export type Filters = {
  tab: 'board' | 'metrics';
  view: ViewId | null;
  status: Status | null;
  tile: TileId | null;
  doctor: string | null;
  referrer: string | null;
  q: string;
  sort: SortKey | null;
  dir: 'asc' | 'desc';
};

export const EMPTY_FILTERS: Filters = { tab: 'board', view: null, status: null, tile: null, doctor: null, referrer: null, q: '', sort: null, dir: 'asc' };

const VIEW_IDS = new Set<string>(PIPELINE_VIEWS.map((v) => v.id));
const TILE_IDS = new Set<string>(TILES.map((t) => t.id));

export function parseFilters(params: URLSearchParams): Filters {
  const view = params.get('view');
  const status = Number(params.get('status'));
  const tile = params.get('tile');
  const sort = params.get('sort');
  const dir = params.get('dir');
  const q = (params.get('q') ?? '').slice(0, 64);
  const parsedView = view && VIEW_IDS.has(view) ? (view as ViewId) : null;
  const parsedStatus = !parsedView && ALL_STATUSES.includes(status as Status) ? (status as Status) : null;
  return {
    tab: params.get('tab') === 'metrics' ? 'metrics' : 'board',
    view: parsedView,
    status: parsedStatus,
    tile: tile && TILE_IDS.has(tile) ? (tile as TileId) : null,
    doctor: params.get('doctor') || null,
    referrer: params.get('referrer') || null,
    q,
    sort: sort && (SORT_KEYS as readonly string[]).includes(sort) ? (sort as SortKey) : null,
    dir: dir === 'desc' ? 'desc' : 'asc',
  };
}

/** Canonical form: a single status equal to a view becomes that view; defaults are omitted. */
export function serialiseFilters(f: Filters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.tab === 'metrics') p.set('tab', 'metrics');
  let view = f.view;
  let status = f.status;
  if (!view && status !== null) {
    const match = PIPELINE_VIEWS.find((v) => v.statuses.length === 1 && v.statuses[0] === status);
    if (match) { view = match.id; status = null; }
  }
  if (view) p.set('view', view);
  else if (status !== null) p.set('status', String(status));
  if (f.tile) p.set('tile', f.tile);
  if (f.doctor) p.set('doctor', f.doctor);
  if (f.referrer) p.set('referrer', f.referrer);
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.sort) { p.set('sort', f.sort); if (f.dir === 'desc') p.set('dir', 'desc'); }
  return p;
}

export function activeStatuses(f: Filters): Set<Status> {
  if (f.view) return new Set(PIPELINE_VIEWS.find((v) => v.id === f.view)!.statuses);
  if (f.status !== null) return new Set([f.status]);
  return new Set();
}

export function activeCount(f: Filters): number {
  return [f.view || f.status !== null, f.tile, f.doctor, f.referrer, f.q.trim()].filter(Boolean).length;
}

export function applyFilters(rows: MatterRow[], f: Filters): MatterRow[] {
  const statuses = activeStatuses(f);
  const out = rows.filter((r) =>
    (statuses.size === 0 || statuses.has(r.status))
    && (!f.tile || tileMatches(f.tile, r))
    && (!f.doctor || r.doctor.slug === f.doctor)
    && (!f.referrer || r.referrer.id === f.referrer)
    && matchesQuery(r, f.q));
  return f.sort ? out : out.toSorted(defaultOrder);
}

export function usePipelineFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(params), [params]);

  // Rewrite unknown or non-canonical values in place, without a history entry.
  useEffect(() => {
    const canonical = serialiseFilters(filters).toString();
    if (canonical !== params.toString()) setParams(serialiseFilters(filters), { replace: true });
  }, [filters, params, setParams]);

  const update = useCallback((patch: Partial<Filters> | ((f: Filters) => Partial<Filters>)) => {
    setParams((prev) => {
      const current = parseFilters(prev);
      const next = { ...current, ...(typeof patch === 'function' ? patch(current) : patch) };
      return serialiseFilters(next);
    });
  }, [setParams]);

  const statuses = useMemo(() => activeStatuses(filters), [filters]);

  return {
    filters,
    statuses,
    update,
    toggleStatus: (status: Status) => update((f) => {
      const set = activeStatuses(f);
      const only = set.size === 1 && set.has(status);
      return only ? { view: null, status: null } : { view: null, status };
    }),
    toggleView: (view: ViewId) => update((f) => (f.view === view ? { view: null, status: null } : { view, status: null })),
    toggleTile: (tile: TileId) => update((f) => ({ tile: f.tile === tile ? null : tile })),
    clearStatuses: () => update({ view: null, status: null }),
    setTab: (tab: Filters['tab']) => update({ tab }),
    setSort: (sort: SortKey | null, dir: Filters['dir'] = 'asc') => update({ sort, dir }),
    clear: () => update({ ...EMPTY_FILTERS, tab: filters.tab }),
  };
}
